import { useState } from "react";
import { ChevronRightIcon, PlusIcon, ShieldCheckIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ListNoResults, ListSearch } from "../components/ListSearch/ListSearch.jsx";
import { EmptyState } from "../components/EmptyState/EmptyState.jsx";
import { ExpansionSection } from "../components/ExpansionSection/ExpansionSection.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PolicyIdentity, policyDisplayTitle, policyHref } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { PolicyStatus } from "../components/PolicyStatus/PolicyStatus.jsx";
import { RecordList } from "../components/RecordList/RecordList.jsx";
import { SampleRecordAction } from "../components/SampleRecordAction/SampleRecordAction.jsx";
import { createSamplePolicy, findSampleClass } from "../domain/sampleRecords.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./PoliciesPage.css";

export function PoliciesPage({ repository }) {
  const application = useApplicationDocument(repository);
  const [search, setSearch] = useState("");
  const [disabledExpanded, setDisabledExpanded] = useState(false);

  async function addSamplePolicy() {
    const nextDocument = createSamplePolicy(application.document);
    if (await application.save(nextDocument)) navigateToHash(policyHref(nextDocument.policies.at(-1).id));
  }

  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <PoliciesLoading />;

  const policies = [...(application.document.policies ?? [])].sort((left, right) => (
    policyDisplayTitle(application.document, left).label.localeCompare(policyDisplayTitle(application.document, right).label, undefined, { sensitivity: "base" })
  ));
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visiblePolicies = policies.filter((policy) => !normalizedSearch || policyDisplayTitle(application.document, policy).label.toLocaleLowerCase().includes(normalizedSearch));
  const enabledPolicies = visiblePolicies.filter((policy) => policy.enabled !== false);
  const disabledPolicies = visiblePolicies.filter((policy) => policy.enabled === false);
  const canAddSample = policies.length === 0 && Boolean(findSampleClass(application.document));

  return <article className="policies-page">
    <PageHeading description="State the allocation rules this portfolio must and should follow." actions={<Button as="a" href="#/policies/new" leadingVisual={PlusIcon}>Add policy</Button>}>Policies</PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {policies.length === 0 ? <PoliciesEmpty onAddSample={canAddSample ? addSamplePolicy : null} saving={application.saving} /> : <>
      <ListSearch ariaLabel="Search policies" clearLabel="Clear policy search" placeholder="Search policies" value={search} visibleCount={visiblePolicies.length} totalCount={policies.length} onChange={(event) => { setSearch(event.target.value); if (event.target.value.trim()) setDisabledExpanded(true); }} onClear={() => setSearch("")} />
      {enabledPolicies.length ? <PolicyList document={application.document} policies={enabledPolicies} /> : null}
      {disabledPolicies.length ? <RecordList className="policy-disabled-list"><ExpansionSection className="policy-disabled-group" expanded={disabledExpanded}>
        <button className="policy-disabled-summary" type="button" aria-expanded={disabledExpanded} onClick={() => setDisabledExpanded((current) => !current)}><ChevronRightIcon aria-hidden="true" /><strong>Disabled policies</strong><span>{disabledPolicies.length}</span></button>
        {disabledExpanded ? <PolicyList document={application.document} policies={disabledPolicies} /> : null}
      </ExpansionSection></RecordList> : null}
      {!visiblePolicies.length ? <ListNoResults heading="No matching policies">Try another policy name.</ListNoResults> : null}
    </>}
  </article>;
}

function PolicyList({ document, policies }) {
  return <RecordList className="policies-list">{policies.map((policy) => <div className="record-row policy-item" key={policy.id}>
    <div className="policy-summary">
      <PolicyIdentity document={document} href={policyHref(policy.id)} policy={policy} variant="subject" />
      <PolicyStatus document={document} policy={policy} />
    </div>
  </div>)}</RecordList>;
}

function PoliciesEmpty({ onAddSample, saving }) {
  return <EmptyState
    icon={ShieldCheckIcon}
    heading="No policies yet"
    description="Add a policy to define a required or preferred allocation range."
    action={<Button as="a" href="#/policies/new" leadingVisual={PlusIcon}>Add your first policy</Button>}
    secondaryAction={onAddSample ? <SampleRecordAction disabled={saving} onSelect={onAddSample}>add a sample Stocks policy</SampleRecordAction> : null}
  />;
}

function PoliciesLoading() {
  return <article className="policies-page"><PageHeading description="Loading policies…">Policies</PageHeading></article>;
}
